import { MEDIA_MESSAGE_LABELS } from "../messaging/mediaMessageLabels";

export const INTERNAL_CHAT_PRIORITY = {
  1: { label: "Low", color: "#64748b", background: "#f1f5f9" },
  2: { label: "Normal", color: "#0891b2", background: "#ecfeff" },
  3: { label: "Important", color: "#2563eb", background: "#eff6ff" },
  4: { label: "High", color: "#ea580c", background: "#fff7ed" },
  5: { label: "Critical", color: "#dc2626", background: "#fef2f2" },
};

export function userDisplayName(user) {
  if (!user) return "Unknown user";
  return [user.name, user.surname].filter(Boolean).join(" ").trim() || user.username || `User #${user.id}`;
}

export function conversationTitle(conversation) {
  return conversation?.type === "system"
    ? conversation.system?.name || "Uzdpart360 System"
    : userDisplayName(conversation?.other_user);
}

export function isServerMessage(message) {
  return message?.id != null && !String(message.id).startsWith("local:");
}

function sameMessage(a, b) {
  if (a?.local_message_id && b?.local_message_id && a.local_message_id === b.local_message_id) return true;
  return isServerMessage(a) && isServerMessage(b) && String(a.id) === String(b.id);
}

function messageOrderValue(message) {
  const created = Date.parse(message?.created_at || "");
  return Number.isFinite(created) ? created : 0;
}

export function upsertMessages(existing = [], incoming = []) {
  const result = [...existing];
  for (const next of incoming.filter(Boolean)) {
    const index = result.findIndex(current => sameMessage(current, next));
    const canonical = isServerMessage(next);
    if (index >= 0) {
      result[index] = {
        ...result[index],
        ...next,
        _status: next._status || (canonical ? "sent" : result[index]._status),
        _error: canonical ? null : (next._error ?? result[index]._error),
      };
    } else {
      result.push({ ...next, _status: next._status || (canonical ? "sent" : "sending") });
    }
  }
  return result.sort((a, b) => {
    const byTime = messageOrderValue(a) - messageOrderValue(b);
    if (byTime !== 0) return byTime;
    if (isServerMessage(a) && isServerMessage(b)) return Number(a.id) - Number(b.id);
    return String(a.local_message_id || a.id).localeCompare(String(b.local_message_id || b.id));
  });
}

export function applyReceipt(messages, { throughMessageId, field, timestamp, currentUserId }) {
  const through = Number(throughMessageId);
  if (!Number.isFinite(through)) return messages;
  return messages.map(message => {
    const id = Number(message.id);
    const mine = Number(message.sender_user_id) === Number(currentUserId);
    if (!mine || !Number.isFinite(id) || id > through) return message;
    if (field === "read_at") {
      return { ...message, delivered_at: message.delivered_at || timestamp, read_at: message.read_at || timestamp };
    }
    return { ...message, delivered_at: message.delivered_at || timestamp };
  });
}

export function sortConversations(conversations = []) {
  return [...conversations].sort((a, b) => {
    if (a.type === "system" && b.type !== "system") return -1;
    if (b.type === "system" && a.type !== "system") return 1;
    const aTime = Date.parse(a.last_message_at || a.last_message?.created_at || 0) || 0;
    const bTime = Date.parse(b.last_message_at || b.last_message?.created_at || 0) || 0;
    return bTime - aTime || Number(b.id) - Number(a.id);
  });
}

export function unreadMessageTotal(conversations = []) {
  return conversations.reduce((total, conversation) => total + Number(conversation.unread_count || 0), 0);
}

export function highestIncomingMessageId(messages = [], currentUserId) {
  return messages.reduce((highest, message) => {
    if (Number(message.recipient_user_id) !== Number(currentUserId) || !isServerMessage(message)) return highest;
    return Math.max(highest, Number(message.id) || 0);
  }, 0);
}

export function lastMessagePreview(message) {
  if (!message) return "No messages yet";
  const media = MEDIA_MESSAGE_LABELS[message.message_type];
  if (media) return `${media[0]} ${message.text_content?.trim() || media[1]}`;
  return message.text_content || "Message";
}
